import { Box, Typography, Divider,useTheme } from '@mui/material';

const renderInlineMarkdown = (text) => {
    if (!text) return null;
    const parts = String(text).split('**');
    return parts.map((part, index) => (
        <Box
            key={`md-inline-${index}`}
            component="span"
            sx={{ fontWeight: index % 2 === 1 ? 600 : 400 }}
        >
            {part}
        </Box>
    ));
};

const renderMarkdown = (text) => {
    if (!text) return null;
    const lines = String(text)
        .replace(/\r\n/g, '\n')
        .replace(/\n{3,}/g, '\n\n')
        .split('\n');
    const blocks = [];
    let listItems = [];

    const flushList = () => {
        if (!listItems.length) return;
        blocks.push(
            <Box key={`md-list-${blocks.length}`} component="ul" sx={{ pl: 3, my: 1 }}>
                {listItems.map((item, index) => (
                    <Box key={`md-li-${index}`} component="li">
                        <Typography variant="body2">{renderInlineMarkdown(item)}</Typography>
                    </Box>
                ))}
            </Box>
        );
        listItems = [];
    };

    lines.forEach((line) => {
        const trimmed = line.trim();
        if (!trimmed) {
            flushList();
            return;
        }

        if (/^[-*]\s+/.test(trimmed) || /^\d+\.\s+/.test(trimmed)) {
            const item = trimmed.replace(/^[-*]\s+/, '').replace(/^\d+\.\s+/, '');
            listItems.push(item);
            return;
        }

        if (/^####\s+/.test(trimmed)) {
            flushList();
            blocks.push(
                <Typography key={`md-h4-${blocks.length}`} variant="subtitle2" color="primary" fontWeight={600} sx={{ mt: 1 }}>
                    {renderInlineMarkdown(trimmed.replace(/^####\s+/, ''))}
                </Typography>
            );
            return;
        }

        if (/^###\s+/.test(trimmed)) {
            flushList();
            blocks.push(
                <Typography key={`md-h3-${blocks.length}`} variant="subtitle1" fontWeight={700} sx={{ mt: 1 }}>
                    {renderInlineMarkdown(trimmed.replace(/^###\s+/, ''))}
                </Typography>
            );
            return;
        }

        if (/^##\s+/.test(trimmed)) {
            flushList();
            blocks.push(
                <Typography key={`md-h2-${blocks.length}`} variant="subtitle1" fontWeight={700} sx={{ mt: 1 }}>
                    {renderInlineMarkdown(trimmed.replace(/^##\s+/, ''))}
                </Typography>
            );
            return;
        }

        if (/^#\s+/.test(trimmed)) {
            flushList();
            blocks.push(
                <Typography key={`md-h1-${blocks.length}`} variant="h6" fontWeight={700} sx={{ mt: 1 }}>
                    {renderInlineMarkdown(trimmed.replace(/^#\s+/, ''))}
                </Typography>
            );
            return;
        }

        flushList();
        blocks.push(
            <Typography key={`md-p-${blocks.length}`} variant="body2" sx={{ mt: 0.5, color: 'text.primary' }}>
                {renderInlineMarkdown(trimmed)}
            </Typography>
        );
    });

    flushList();
    return blocks;
};

const sanitizeAnalysisText = (text) => {
    if (!text) return '';
    const lines = String(text).split('\n');
    const metricKeywords = [
        'impressions',
        'clicks',
        'conversions',
        'ctr',
        'cpc',
        'cpm',
        'roas',
        'revenue',
        'cost',
        'avg gift',
        'conversion rate',
        'return on ad spend',
        'total',
    ];
    return lines
        .filter((line) => {
            const trimmed = line.trim().toLowerCase();
            if (!trimmed) return true;
            if (/^\s*-\s*\*\*/.test(trimmed)) {
                return !metricKeywords.some((k) => trimmed.includes(k));
            }
            if (/^[-*]\s+/.test(trimmed) || /^\d+\.\s+/.test(trimmed)) {
                return !metricKeywords.some((k) => trimmed.includes(k));
            }
            if (/^\*\*.*\*\*:/.test(trimmed)) {
                return !metricKeywords.some((k) => trimmed.includes(k));
            }
            if (metricKeywords.some((k) => trimmed.includes(k) && /[\d$%]/.test(trimmed))) {
                return false;
            }
            return true;
        })
        .join('\n');
};

const parseSections = (text) => {
    if (!text) return [];
    const lines = String(text).split('\n');
    const sections = [];
    let current = null;

    const pushCurrent = () => {
        if (!current) return;
        const cleaned = current.lines
            .map((line) => line.replace(/^[-*]\s+/, '').replace(/^\d+\.\s+/, '').trim())
            .filter(Boolean);
        sections.push({
            title: current.title,
            bullets: cleaned.slice(0, 4),
        });
        current = null;
    };

    lines.forEach((line) => {
        const trimmed = line.trim();
        if (/^###\s+/.test(trimmed)) {
            pushCurrent();
            current = { title: trimmed.replace(/^###\s+/, ''), lines: [] };
            return;
        }
        if (!current) return;
        if (trimmed) current.lines.push(trimmed);
    });

    pushCurrent();
    return sections;
};

const stripHtml = (html) => {
    if (!html) return '';
    return String(html)
        .replace(/<style[\s\S]*?<\/style>/gi, '')
        .replace(/<script[\s\S]*?<\/script>/gi, '')
        .replace(/<\/?[^>]+(>|$)/g, '')
        .replace(/\s+/g, ' ')
        .trim();
};

const normalizeHighlights = (items) => {
    if (!Array.isArray(items)) return [];
    return items
        .map((item) => (typeof item === 'string' ? item : item?.text || ''))
        .map((item) => stripHtml(item))
        .map((item) => item.trim())
        .filter(Boolean)
        .slice(0, 6);
};

const StackAdaptAnalysis = ({ analysis }) => {
    const theme = useTheme();
    const analysisJson = analysis?.json || null;
    const recommendations = normalizeHighlights(analysisJson?.recommendations);
    const insights = normalizeHighlights(analysisJson?.insights);
    const highlights = normalizeHighlights(analysisJson?.highlights);
    const risks = normalizeHighlights(analysisJson?.risks);
    const cleanedFallback = sanitizeAnalysisText(analysis?.text || analysisJson?.text || '');
    const sections = parseSections(cleanedFallback);

    const hasAnyContent = recommendations.length || insights.length || highlights.length || risks.length || sections.length;
    if (!hasAnyContent && !analysis) {
        return null;
    }

    return (
        <Box sx={{ mt: 2, pr: 1, pl: 1 }}>
            {recommendations.length || insights.length || highlights.length || risks.length ? (
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr 1fr' }, gap: 1 }}>

                    {insights.length ? (
                        <Box
                            sx={{
                                border: '1px solid rgba(0,0,0,0.08)',
                                borderRadius: 1,
                                p: 1.5,
                            }}
                        >
                            <Typography variant="subtitle2" color="primary" fontWeight={700}>
                                Insights
                            </Typography>
                            <Divider sx={{ my: 0.75 }} />
                            <Box component="ul" sx={{ pl: 3, my: 0.5 }}>
                                {insights.map((item, index) => (
                                    <Box key={`insight-${index}`} component="li">
                                        <Typography variant="body2">{item}</Typography>
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                    ) : null}
                    {highlights.length ? (
                        <Box
                            sx={{
                                border: '1px solid rgba(0,0,0,0.08)',
                                borderRadius: 1,
                                p: 1.5,
                            }}
                        >
                            <Typography variant="subtitle2" color="primary" fontWeight={700}>
                                Highlights
                            </Typography>
                            <Divider sx={{ my: 0.75 }} />
                            <Box component="ul" sx={{ pl: 3, my: 0.5 }}>
                                {highlights.map((item, index) => (
                                    <Box key={`highlight-${index}`} component="li">
                                        <Typography variant="body2">{item}</Typography>
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                    ) : null}
                    {risks.length ? (
                        <Box
                            sx={{
                                border: '1px solid rgba(0,0,0,0.08)',
                                borderRadius: 1,
                                p: 1.5,
                            }}
                        >
                            <Typography variant="subtitle2" color="primary" fontWeight={700}>
                                Risks
                            </Typography>
                            <Divider sx={{ my: 0.75 }} />
                            <Box component="ul" sx={{ pl: 3, my: 0.5 }}>
                                {risks.map((item, index) => (
                                    <Box key={`risk-${index}`} component="li">
                                        <Typography variant="body2">{item}</Typography>
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                    ) : null}
                    {recommendations.length ? (
                        <Box
                            sx={{
                                border: '1px solid rgba(0,0,0,0.08)',
                                backgroundColor: `${theme.palette.primary.main}22`,
                                borderRadius: 1,
                                p: 1.5,
                            }}
                        >
                            <Typography variant="subtitle2" color="textPrimary" fontWeight={700}>
                                Recommendations
                            </Typography>
                            <Divider sx={{ my: 0.75 }} />
                            <Box component="ul" sx={{ pl: 3, my: 0.5 }}>
                                {recommendations.map((item, index) => (
                                    <Box key={`recommendations-${index}`} component="li">
                                        <Typography variant="body2">{item}</Typography>
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                    ) : null}
                </Box>
            ) : sections.length ? (
                <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1 }}>
                    {sections.map((section, index) => (
                        <Box
                            key={`analysis-card-${index}`}
                            sx={{
                                border: '1px solid rgba(0,0,0,0.08)',
                                borderRadius: 1,
                                p: 1.5,
                            }}
                        >
                            <Typography variant="subtitle2" color="primary" fontWeight={700}>
                                {section.title}
                            </Typography>
                            <Divider sx={{ my: 0.75 }} />
                            {section.bullets.length ? (
                                <Box component="ul" sx={{ pl: 3, my: 0.5 }}>
                                    {section.bullets.map((item, itemIndex) => (
                                        <Box key={`analysis-bullet-${index}-${itemIndex}`} component="li">
                                            <Typography variant="body2">{renderInlineMarkdown(item)}</Typography>
                                        </Box>
                                    ))}
                                </Box>
                            ) : (
                                <Typography variant="body2" color="text.secondary">No highlights.</Typography>
                            )}
                        </Box>
                    ))}
                </Box>
            ) : (
                <Box
                    sx={{
                        border: '1px solid rgba(0,0,0,0.08)',
                        borderRadius: 1,
                        p: 1.5,
                    }}
                >
                    <Typography variant="subtitle2" color="primary" fontWeight={700}>
                        Analysis
                    </Typography>
                    <Divider sx={{ my: 0.75 }} />
                    <Typography variant="body2" color="text.secondary">
                        No analysis available for this date range.
                    </Typography>
                </Box>
            )}
        </Box>
    );
};

export default StackAdaptAnalysis;
